/**
 * Custom @nuxt/image provider for Aprimo assets delivered via Fastly Image Optimizer.
 *
 * It appends Fastly IO query params (width/height/fit/dpr/format/quality) to the
 * Aprimo asset URL so the browser receives a right-sized, modern-format, DPR-aware
 * image with a responsive `srcset` — instead of the raw full-resolution original.
 *
 * Opt in per image:  <NuxtImg provider="fastlyAprimo" :src="aprimo.url" :width="665" sizes="..." />
 *
 * NON-BREAKING by design: if the asset host is NOT fronted by a Fastly service with
 * Image Optimizer enabled, the extra query params are simply ignored and the original
 * image is served (i.e. same behaviour as today, never a broken image).
 *
 * If your Aprimo delivery URLs are not already on your Fastly image domain, set
 * NUXT_PUBLIC_FASTLY_IMAGE_HOST environment variable to rewrite the host so the
 * params take effect.
 *
 * Fastly IO reference: https://developer.fastly.com/reference/io/
 */

// Read from environment variable for runtime configurability and testability.
// TODO(180medical): confirm whether Aprimo delivery URLs are already served through
// your Fastly Image Optimizer. If not, set NUXT_PUBLIC_FASTLY_IMAGE_HOST to your
// Fastly image host to rewrite the host (the asset path is preserved).
function getFastlyImageHost() {
  // eslint-disable-next-line node/prefer-global/process
  return (typeof process !== 'undefined' && process.env?.NUXT_PUBLIC_FASTLY_IMAGE_HOST) || ''
}

interface ImageModifiers {
  width?: number | string
  height?: number | string
  fit?: string
  format?: string
  quality?: number | string
  dpr?: number | string
  [key: string]: unknown
}

interface GetImageOptions {
  modifiers?: ImageModifiers
  fastlyImageHost?: string // For testing: override the env var
}

export function getImage(src: string, { modifiers = {}, fastlyImageHost }: GetImageOptions = {}) {
  const {
    width,
    height,
    fit = 'bounds', // 'bounds' = scale within the box (respect ratio); 'cover'/'crop' = fill/crop
    format = 'auto', // negotiate webp/avif via the Accept header
    quality = 80,
    dpr,
  } = modifiers

  const params = new URLSearchParams()
  if (width)
    params.set('width', String(width))
  if (height)
    params.set('height', String(height))
  if (fit)
    params.set('fit', fit)
  if (format)
    params.set('format', format)
  if (quality)
    params.set('quality', String(quality))
  if (dpr)
    params.set('dpr', String(dpr))

  let url = src

  // Optionally rewrite the host onto the Fastly IO domain (path is preserved).
  const imageHost = fastlyImageHost ?? getFastlyImageHost()
  if (imageHost && /^https?:\/\//i.test(src)) {
    try {
      const parsed = new URL(src)
      url = `${imageHost.replace(/\/$/, '')}${parsed.pathname}${parsed.search}`
    }
    catch {
      // Malformed URL: fall back to the original src
      url = src
    }
  }

  const query = params.toString()
  if (!query)
    return { url }

  const separator = url.includes('?') ? '&' : '?'
  return { url: `${url}${separator}${query}` }
}

// @nuxt/image (v2) expects a provider's default export to be a `setup` function
// that returns the runtime instance ({ getImage }). Built-in providers do this via
// defineProvider(); this is the equivalent without importing the internal helper.
export default () => ({ getImage })
